'use client';

import React, { Fragment, useEffect, useState } from 'react';

// Import SVG icons as React components
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { useGetVendorItem } from '@/common/apis/storefronts/v1/get-vendor.api';
import Button from '@/common/components/button';
import { PopUpModal } from '@/common/components/modal/popupModal.component';
import ShareButton from '@/common/components/share-button';
import { stringFilters } from '@/common/utils/stringFilters.utility';

import InstagramIcon from '~/public/assets/images/svg/shop-instagram.svg';
import PhoneIcon from '~/public/assets/images/svg/shop-phone.svg';
import TelegramIcon from '~/public/assets/images/svg/shop-telegram.svg';
import WhatsAppIcon from '~/public/assets/images/svg/shop-whatsapp.svg';

export interface StoreInfoProps {
  className?: string;
}

export interface VendorSocialMedias {
  id: string | null;
  logo: string;
  link: string;
  coloredLogo: string;
  title: string;
}

export default function StoreInfo({ className = '' }: StoreInfoProps) {
  const [socialMedia, setSocialMedia] = useState<VendorSocialMedias[]>([]);
  const [showPopUp, setShowPopup] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<VendorSocialMedias | null>(
    null
  );
  const pathName = usePathname();

  const pathSegments = pathName.slice(1).split('/');
  const { data } = useGetVendorItem({
    query: { identifier: pathSegments[0] },
    disableToast: true,
    enabled: false,
  });

  useEffect(() => {
    let list: VendorSocialMedias[] = [];
    if (data?.data)
      list = [
        {
          id: data.data.instagramAccount,
          link: `${data.data.instagramAccount}`,
          logo: '/assets/images/svg/shop-instagram.svg',
          coloredLogo: '/assets/images/svg/store_instagram.svg',
          title: 'اینستاگرام فروشگاه',
        },
        {
          id: data.data.whatsAppAccount,
          link: `${data.data.whatsAppAccount}`,
          logo: '/assets/images/svg/shop-whatsapp.svg',
          coloredLogo: '/assets/images/svg/store_whatsapp.svg',
          title: 'واتس‌اپ فروشگاه',
        },
        {
          id: data.data.telegramAccount,
          link: `${data.data.telegramAccount}`,
          logo: '/assets/images/svg/shop-telegram.svg',
          coloredLogo: '/assets/images/svg/store_telegram.svg',
          title: 'تلگرام فروشگاه',
        },
        {
          id: data.data.shopPhoneNumber,
          link: `tel:${data.data.shopPhoneNumber}`,
          logo: '/assets/images/svg/shop-phone.svg',
          coloredLogo: '/assets/images/svg/store_phone.svg',
          title: 'شماره تماس فروشگاه',
        },
      ];
    setSocialMedia(list);
  }, [data]);

  return (
    <div
      className={`flex w-full flex-row items-center justify-between py-4 ${className}`}
    >
      {data?.data?.vendorMedia?.vendorLogo && (
        <Image
          alt={data?.data?.vendorName}
          className="ml-2 block  w-[80px] rounded-xl border-[3px] border-white"
          src={data?.data?.vendorMedia.vendorLogo.url}
          width={80}
          height={80}
        />
      )}
      <div className="flex grow flex-col justify-start">
        <h2 className="text-nowrap text-sm font-semibold text-black-default lg:!text-lg lg:!font-semibold lg:!text-[#174170]">
          {stringFilters(data?.data?.vendorName || '', { removeEmojis: true })}
        </h2>
        <p className="my-2 hidden w-full text-right text-base font-normal text-[#191308] lg:block lg:max-w-[708px]">
          {data?.data?.shortDescription || data?.data?.aboutMe}
        </p>
      </div>
      <div className="ltr flex flex-row items-center justify-between gap-x-3 ">
        <ShareButton
          name={data?.data?.vendorName || ''}
          pathName={pathName}
          className="hidden !bg-white !text-secondary-300 lg:flex lg:h-14 lg:w-14"
        />
        {socialMedia.map((media) => (
          <Fragment key={media.title}>
            {media.id && (
              <Button
                className="relative flex h-14 w-14 items-center justify-center !bg-white"
                onClick={() => {
                  setSelectedMedia(media);

                  setShowPopup((toggle) => !toggle);
                }}
              >
                <div className="flex h-14 w-14 items-center justify-center">
                  {media.title.includes('اینستاگرام') && (
                    <InstagramIcon className=" " />
                  )}
                  {media.title.includes('واتس‌اپ') && <WhatsAppIcon />}
                  {media.title.includes('تلگرام') && <TelegramIcon />}
                  {media.title.includes('شماره تماس') && <PhoneIcon />}
                </div>
              </Button>
            )}
          </Fragment>
        ))}
      </div>
      <PopUpModal
        className="w-[300px]"
        onCancel={() => {
          setShowPopup((toggle) => !toggle);
        }}
        open={showPopUp}
      >
        <div className="flex h-64 w-full  flex-col items-center justify-center pt-8 ">
          <div className="mb-5 flex h-[120px] w-[120px] items-center justify-center rounded-[20px] bg-coconut-900 ">
            <Image
              src={selectedMedia?.coloredLogo || ''}
              alt={`لوگو ${selectedMedia?.title || 'شبکه اجتماعی'}`}
              width={80}
              height={80}
            />
          </div>
          <h2 className="mb-[12px] text-lg font-semibold text-secondary-default">
            {selectedMedia?.title}
          </h2>
          <Link
            href={selectedMedia?.link || ''}
            className="w-full !bg-white  text-center text-base font-medium"
          >
            {selectedMedia?.link?.startsWith('tel:')
              ? selectedMedia.link.replace('tel:', '')
              : selectedMedia?.link}
          </Link>
        </div>
      </PopUpModal>
    </div>
  );
}
